<template>
    <!-- Show Permission Denied when Point System is NONACTIVE -->
    <PermissionDenied v-if="!isPointSystemActive" />

    <!-- Show normal content when Point System is ACTIVE -->
    <div v-else class="page-container">
        <!-- Desktop View -->
        <div class="desktop-view" v-if="!isMobile">
            <div class="page-header">
                <div class="header-content">
                    <h1 class="page-title">Special Deals</h1>
                    <p class="page-subtitle">Redeem exclusive vouchers with your points</p>
                </div>
            </div>

            <div class="content-wrapper">
                <!-- Main Content -->
                <div class="main-content">
                    <!-- Skeleton Loading State (only on first load) -->
                    <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="deals" :count="6" :is-grid="true" />

                    <!-- Voucher List -->
                    <div v-if="!(page.isLoading && page.isFirstLoad) && displayedVoucher.length > 0" class="deals-list desktop">
                        <div 
                            class="deal-container" 
                            v-for="(voucher, index) in displayedVoucher" 
                            :key="index"
                            @click="() => showVoucherDetail(voucher)"
                        >
                            <div class="deal-cost">
                                <span class="deal-cost-text">{{ voucher.point }} POIN</span>
                            </div>
                            <div class="img-container">
                                <img 
                                    :src="voucher.avatar?.image_url || voucher.image_url || '/img/member-img/offer-example.svg'" 
                                    alt="" 
                                    class="deal-img"
                                >
                            </div>

                            <div class="deal-header">
                                <div class="corporate-logo">
                                    <img 
                                        v-if="getMerchantLogo(voucher)"
                                        :src="getMerchantLogo(voucher)" 
                                        :alt="voucher.merchant?.name || 'Corporate Logo'"
                                    />
                                    <div v-else class="logo-placeholder">
                                        {{ getInitials(voucher) }}
                                    </div>
                                </div>

                                <div class="deal-title-container">
                                    <h3 class="deal-title">
                                        {{ voucher.name }}
                                    </h3>
                                    <div class="deal-description">
                                        <p class="marquee" :data-text="getMerchantMarqueeText(voucher)">
                                            {{ getMerchantMarqueeText(voucher) }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="deal-dash-line"/>

                            <div class="deal-status">
                                <span class="deal-status-icon" style="-webkit-mask-image: url('/img/member-icon/voucher-status.svg'); mask-image: url('/img/member-icon/voucher-status.svg')"></span>
                                <span class="deal-status-text">{{ voucher.is_limited ? voucher.stock : 'Limited Offers' }} available</span>
                            </div>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <no-items v-if="!(page.isLoading && page.isFirstLoad) && displayedVoucher.length === 0" message="No vouchers available at the moment" transparent />

                    <!-- Pagination -->
                    <member-pagination 
                        v-if="!page.isLoading && displayedVoucher.length" 
                        :page="meta.page" 
                        :total="meta.total" 
                        @callback="navPage"
                    />
                </div>

                <!-- Sidebar Filter & Sort -->
                <div class="sidebar">
                    <!-- Filter Section -->
                    <div class="filter-card">
                        <h3 class="card-title">Filter</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select desktop">
                                <select v-model="filterOptions.branch" :disabled="loading">
                                    <option value="">All Branch</option>
                                    <option 
                                        v-for="branch in uniqueBranches" 
                                        :key="branch.id" 
                                        :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select desktop">
                                <select v-model="filterOptions.merchant" :disabled="loading">
                                    <option value="">All Merchant</option>
                                    <option 
                                        v-for="merchant in filteredMerchants" 
                                        :key="merchant.id" 
                                        :value="merchant.id">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn-apply" :disabled="loading">Apply Filter</button>
                            <button @click="clearFilter" class="btn-clear" :disabled="loading">Clear Filter</button>
                        </div>
                    </div>

                    <!-- Sort Section -->
                    <div class="filter-card">
                        <h3 class="card-title">Sort</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Sort By</label>
                            <div class="custom-select desktop">
                                <select v-model="sortOptions.sortBy" :disabled="loading">
                                    <option 
                                        v-for="option in sortByOptions" 
                                        :key="option.value" 
                                        :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Sort Direction</label>
                            <div class="custom-select desktop">
                                <select v-model="sortOptions.sortDirection" :disabled="loading">
                                    <option 
                                        v-for="option in sortDirectionOptions" 
                                        :key="option.value" 
                                        :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applySort" class="btn-apply" :disabled="loading">Apply Sort</button>
                            <button @click="clearSort" class="btn-clear" :disabled="loading">Clear Sort</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile View -->
        <div class="mobile-view" v-else>
            <div class="top-background">
                <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="111" cy="-84" rx="219" ry="139" fill="currentColor"/>
                </svg>
            </div>
            
            <div class="mobile-header">
                <h2 class="mobile-title">Special Deals</h2>
                <div class="mobile-actions">
                    <button @click="openModal('filter')" class="filter-button" :disabled="loading">
                        <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
                    </button>
                    <button @click="openModal('sort')" class="filter-button" :disabled="loading">
                        <img src="/img/member-icon/sort-icon.svg" alt="sort-icon" width="14" height="14">
                    </button>
                </div>
            </div>
            
            <!-- Skeleton Loading State (Mobile - only on first load) -->
            <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="deals" :count="4" :is-grid="true" />

            <!-- Voucher List -->
            <div class="deals-list" v-if="!(page.isLoading && page.isFirstLoad) && displayedVoucher.length > 0">
                <div 
                    class="deal-container" 
                    v-for="(voucher, index) in displayedVoucher" 
                    :key="index"
                    @click="() => showVoucherDetail(voucher)"
                >
                    <div class="deal-cost">
                        <span class="deal-cost-text">{{ voucher.point }} POIN</span>
                    </div>
                    <div class="img-container">
                        <img 
                            :src="voucher.avatar?.image_url || voucher.image_url || '/img/member-img/offer-example.svg'" 
                            alt="" 
                            class="deal-img"
                        >
                    </div>

                    <div class="deal-header">
                        <div class="corporate-logo">
                            <img 
                                v-if="getMerchantLogo(voucher)"
                                :src="getMerchantLogo(voucher)" 
                                :alt="voucher.merchant?.name || 'Corporate Logo'"
                            />
                            <div v-else class="logo-placeholder">
                                {{ getInitials(voucher) }}
                            </div>
                        </div>

                        <div class="deal-title-container">
                            <h3 class="deal-title">
                                {{ voucher.name }}
                            </h3>
                            <div class="deal-description">
                                <p class="marquee" :data-text="getMerchantMarqueeText(voucher)">
                                    {{ getMerchantMarqueeText(voucher) }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="deal-dash-line"/>

                    <div class="deal-status">
                        <span class="deal-status-icon" style="-webkit-mask-image: url('/img/member-icon/voucher-status.svg'); mask-image: url('/img/member-icon/voucher-status.svg')"></span>
                        <span class="deal-status-text">{{ voucher.is_limited ? voucher.stock : 'Limited Offers' }} available</span>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <no-items v-if="!(page.isLoading && page.isFirstLoad) && displayedVoucher.length === 0" message="No vouchers available at the moment" transparent />

            <!-- Pagination (Mobile) -->
            <member-pagination 
                v-if="!page.isLoading && displayedVoucher.length" 
                :page="meta.page" 
                :total="meta.total" 
                @callback="navPage"
            />
        </div>

        <!-- Filter Modal (Mobile Only) -->
        <div v-if="isFilterModalOpen" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Filter</h3>
                    <button @click="closeModal" class="close-button">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="filter-group">
                        <label class="filter-label">Unit / Branch</label>
                        <div class="custom-select">
                            <select v-model="filterOptions.branch" :disabled="loading">
                                <option value="">All Branch</option>
                                <option 
                                    v-for="branch in uniqueBranches" 
                                    :key="branch.id" 
                                    :value="branch.id">
                                    {{ branch.name }}
                                </option>
                            </select>
                            <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <div class="filter-group">
                        <label class="filter-label">Merchant / Outlet</label>
                        <div class="custom-select">
                            <select v-model="filterOptions.merchant" :disabled="loading">
                                <option value="">All Merchant</option>
                                <option 
                                    v-for="merchant in filteredMerchants" 
                                    :key="merchant.id" 
                                    :value="merchant.id">
                                    {{ merchant.name }}
                                </option>
                            </select>
                            <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <div class="button-group">
                        <button @click="applyFilter" class="btn-apply" :disabled="loading">Apply Filter</button>
                        <button @click="clearFilter" class="btn-clear" :disabled="loading">Clear Filter</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sort Modal (Mobile Only) -->
        <div v-if="isSortModalOpen" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Sort</h3>
                    <button @click="closeModal" class="close-button">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="filter-group">
                        <label class="filter-label">Sort By</label>
                        <div class="custom-select">
                            <select v-model="sortOptions.sortBy" :disabled="loading">
                                <option 
                                    v-for="option in sortByOptions" 
                                    :key="option.value" 
                                    :value="option.value">
                                    {{ option.text }}
                                </option>
                            </select>
                            <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <div class="filter-group">
                        <label class="filter-label">Sort Direction</label>
                        <div class="custom-select">
                            <select v-model="sortOptions.sortDirection" :disabled="loading">
                                <option 
                                    v-for="option in sortDirectionOptions" 
                                    :key="option.value" 
                                    :value="option.value">
                                    {{ option.text }}
                                </option>
                            </select>
                            <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <div class="button-group">
                        <button @click="applySort" class="btn-apply" :disabled="loading">Apply Sort</button>
                        <button @click="clearSort" class="btn-clear" :disabled="loading">Clear Sort</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Voucher Detail Modal -->
        <VoucherDetailModal
            v-if="selectedVoucher"
            :voucher="selectedVoucher"
            @close="closeVoucherDetail"
            @redeem="handleRedeem"
        />

        <!-- Redeem Status Modal -->
        <VoucherRedeemStatusModal
            v-if="redeemStatus"
            :status="redeemStatus"
            @close="closeRedeemStatus"
        />
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import VoucherDetailModal from '@/components/member/VoucherDetailModal.vue'
import VoucherRedeemStatusModal from '@/components/member/VoucherRedeemStatusModal.vue'
import SkeletonLoader from '@/components/member/SkeletonLoader.vue'
import NoItems from '@/components/member/NoItems.vue'
import MemberPagination from '@/components/member/MemberPagination.vue'
import PermissionDenied from '@/views/PermissionDenied.vue'
import { usePointSystem } from '@/composables/pointsystem'
import { useModalHistory } from '@/composables/useModalHistory'
import { setSwipeBackEnabled } from '@/services/swipeBack'
import { useToast } from '@/composables/useToast'

export default {
  name: 'MemberDeals',
  components: {
      VoucherDetailModal,
      VoucherRedeemStatusModal,
      SkeletonLoader,
      NoItems,
      MemberPagination,
      PermissionDenied
  },
  setup() {
      const { isPointSystemActive } = usePointSystem()
      const store = useStore()
      const branches = ref([])
      const merchants = ref([])
      const isFilterModalOpen = ref(false)
      const isSortModalOpen = ref(false)
      const branchMerchantList = ref([])
      const fullResponseData = ref([])
      const filteredVoucherState = ref([])
      const selectedVoucher = ref(null)
      const redeemStatus = ref(null)
      const loading = ref(false)
      const isMobile = ref(window.innerWidth < 992)

      // Modal History (Back Button Handler)
      const { pushModalHistory: pushFilterModal, popModalHistory: popFilterModal } = useModalHistory({
          isVisible: isFilterModalOpen,
          onClose: () => { isFilterModalOpen.value = false },
          stateKey: 'deals-filter-modal'
      })

      const { pushModalHistory: pushSortModal, popModalHistory: popSortModal } = useModalHistory({
          isVisible: isSortModalOpen,
          onClose: () => { isSortModalOpen.value = false },
          stateKey: 'deals-sort-modal'
      })

      const isVoucherDetailOpen = ref(false)
      const { pushModalHistory: pushVoucherModal, popModalHistory: popVoucherModal } = useModalHistory({
          isVisible: isVoucherDetailOpen,
          onClose: () => {
              isVoucherDetailOpen.value = false
              selectedVoucher.value = null
          },
          stateKey: 'deals-voucher-detail'
      })

      // Reactive state
      const page = reactive({
          isLoading: true,
          output: false,
          isFirstLoad: true
      })

      const selected = reactive({
          branch_id: '',
          merchant_id: ''
      })

      const filterOptions = reactive({
          branch: '',
          merchant: ''
      })

      const sortOptions = reactive({
          sortBy: 'pointRequirement',
          sortDirection: 'asc'
      })

      // Options
      const sortByOptions = [
          { value: 'pointRequirement', text: 'Point Requirement' }
      ]

      const sortDirectionOptions = [
          { value: 'asc', text: 'Lowest to Highest' },
          { value: 'desc', text: 'Highest to Lowest' }
      ]

      const meta = computed(() => store.state.ClientDeals.meta)

      // Computed properties
      const filteredMerchants = computed(() => {
            if (filterOptions.branch) {
                const selectedBranch = branchMerchantList.value.find(branch => branch.id === filterOptions.branch);
                return selectedBranch ? selectedBranch.merchants : [];
            }

            let allMerchants = [];
            branchMerchantList.value.forEach(branch => {
                allMerchants = allMerchants.concat(branch.merchants);
            });
            return allMerchants;
        });


        const uniqueBranches = computed(() => {
            return branchMerchantList.value.map(branch => ({
            id: branch.id,
            name: branch.name
            }));
        });

      const memberPoint = computed(() => {
          const deals = store.state.ClientDeals.deals
          return deals.length > 0 ? deals[0].memberPoint : null
      })

      const deals = computed(() => {
          const trueStatusDeals = store.state.ClientDeals.deals
              .filter(item => item.status)
              .sort((a, b) => a.point - b.point)
          const falseStatusDeals = store.state.ClientDeals.deals
              .filter(item => !item.status)
              .sort((a, b) => a.point - b.point)
          return [...trueStatusDeals, ...falseStatusDeals]
      })

        const displayedVoucher = computed(() => {
            return filteredVoucherState.value;
        });

      // Methods
      const loadBranches = async () => {
          await store.dispatch('BranchMerchant/read').then(() => {
              branches.value = [
                  { value: "all", text: "From all branches" },
                  ...store.state.BranchMerchant.entities.map(branch => ({
                      value: branch.id,
                      text: branch.name,
                      merchants: branch.merchant
                  }))
              ]
              selected.branch_id = "all"
              loadState()
          })
      }

      const loadState = () => {
        page.isLoading = true;
        const filter = {};

        if (filterOptions.branch) {
            filter.branch_id = filterOptions.branch;
        }

        if (filterOptions.merchant) {
            filter.merchant_id = filterOptions.merchant;
        }

        if (sortOptions.sortBy && sortOptions.sortDirection) {
            filter.sortBy = sortOptions.sortBy;
            filter.sortDirection = sortOptions.sortDirection;
        }

        store.commit('ClientDeals/add_filter', { filter });

        store.dispatch('ClientDeals/read')
        .then(response => {
            const newMeta = response.data.meta || response.meta || (response.data.data && response.data.data.meta);
            if (newMeta) {
                // Use switch_page to update meta as it's a known mutation in other modules
                store.commit('ClientDeals/switch_page', { meta: newMeta });
            }
            fullResponseData.value = response.data.data.voucher;
            // Filter out expired vouchers and use only valid ones
            const now = new Date();
            filteredVoucherState.value = response.data.data.voucher.filter(voucher => {
                if (!voucher.expiration) return true; // Keep if no expiration set
                const expirationDate = new Date(voucher.expiration);
                return expirationDate > now; // Only show non-expired vouchers
            });

            const branchesMap = new Map();

            response.data.data.branches.forEach(branchItem => {
                branchesMap.set(branchItem.id, {
                    id: branchItem.id,
                    name: branchItem.name,
                    merchants: branchItem.merchants.map(merchant => ({
                        id: merchant.id,
                        name: merchant.name
                    }))
                });
            });

            branchMerchantList.value = Array.from(branchesMap.values());

            if (isMobile.value && (filterOptions.branch || filterOptions.merchant || sortOptions.sortBy)) {
                closeModal();
            }

        })
        .catch(err => {
            console.error("Error loading state:", err);
        })
        .finally(() => {
            page.isLoading = false;
            page.output = true;
            page.isFirstLoad = false;
        });
    }

      const loadMerchant = () => {
          const branch_id = selected.branch_id
          const selected_branch = [...branches.value].find(branch => branch.value == branch_id)
          merchants.value = selected.branch_id == "all" ? '' : 
              selected_branch.merchants.map(v => ({ value: v.id, text: v.name }))
      }

      const applySort = () => {
            applyFilter();
            closeModal();
        };

        const applyFilter = () => {
            const _meta = { ...meta.value, page: 1 };
            store.commit('ClientDeals/switch_page', { meta: _meta });
            loadState();
        };

      const clearSort = () => {
            sortOptions.sortBy = 'pointRequirement';
            sortOptions.sortDirection = 'asc'; 
            store.commit('ClientDeals/clear_filter')
            loadState()
            closeModal();
        };

      const clearFilter = () => {
          if (isMobile.value) {
              filterOptions.branch = ''
              filterOptions.merchant = ''
              store.commit('ClientDeals/clear_filter')
              loadState()
              closeModal()
          } else {
                filterOptions.branch = ''
                filterOptions.merchant = ''
                store.commit('ClientDeals/clear_filter')
                loadState()
          }
      }

      const filter = () => {
          const data_filter = selected.merchant_id !== '' ? 
              { merchant_id: selected.merchant_id } : 
              { branch_id: selected.branch_id }
          store.commit('ClientDeals/add_filter', { filter: data_filter })
          loadState()
      }

      const navPage = (page) => {
          const _meta = { ...meta.value, page }
          store.commit('ClientDeals/switch_page', { meta: _meta })
          loadState()
      }

      const openModal = (type) => {
          if (type === 'filter') {
              isFilterModalOpen.value = true
              isSortModalOpen.value = false
              pushFilterModal()
          } else if (type === 'sort') {
              isSortModalOpen.value = true
              isFilterModalOpen.value = false
              pushSortModal()
          }
      }

      const closeModal = () => {
          if (isFilterModalOpen.value) {
              isFilterModalOpen.value = false
              popFilterModal()
          }
          if (isSortModalOpen.value) {
              isSortModalOpen.value = false
              popSortModal()
          }
      }

      // Voucher detail and redeem methods
      const showVoucherDetail = (voucher) => {
          selectedVoucher.value = voucher
          isVoucherDetailOpen.value = true
          pushVoucherModal()
      }

      const closeVoucherDetail = () => {
          selectedVoucher.value = null
          isVoucherDetailOpen.value = false
          popVoucherModal()
      }

      const handleRedeem = async (voucher) => {
          setSwipeBackEnabled(false)
          const isOnline = store.state.network?.isOnline ?? true;
          if (!isOnline) {
              const { showToast } = useToast();
              showToast({
                  type: 'warning',
                  title: 'No Connection',
                  message: 'You are currently offline. Please check your internet connection to redeem vouchers.',
              });
              return;
          }
          loading.value = true
          redeemStatus.value = 'loading'
          
          // Tutup modal detail segera setelah tombol redeem diklik
          closeVoucherDetail()
          
          try {
              const voucherId = voucher?.id ?? voucher?.voucher_id
              
              if (!voucherId) {
                  redeemStatus.value = null
                  setSwipeBackEnabled(true)
                  return
              }

              const payload = {
                  merchant_id: voucher.merchant_id,
                  memberDeal: true,
                  quantity: 1,
                  voucher_id: voucherId,
                  expiration: voucher.expiration,
                  cancelable: voucher.cancelable,
              }

              // Tambahkan cancelable_before jika voucher dapat dibatalkan
              if (voucher.cancelable === 1 && voucher.cancellation_cutoff) {
                  const expirationDate = new Date(voucher.expiration)
                  const cutoffMs = voucher.cancellation_cutoff * 1000
                  const cancelableBeforeDate = new Date(expirationDate.getTime() - cutoffMs)

                  const year = cancelableBeforeDate.getFullYear()
                  const month = String(cancelableBeforeDate.getMonth() + 1).padStart(2, '0')
                  const day = String(cancelableBeforeDate.getDate()).padStart(2, '0')
                  const hours = String(cancelableBeforeDate.getHours()).padStart(2, '0')
                  const minutes = String(cancelableBeforeDate.getMinutes()).padStart(2, '0')
                  const seconds = String(cancelableBeforeDate.getSeconds()).padStart(2, '0')

                  payload.cancelable_before = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
              }

              await store.dispatch('ClientVoucher/redeem', { data: payload })
              redeemStatus.value = 'success'
              // Reload vouchers after successful redeem
              await loadState()
          } catch (error) {
              console.error('Redeem error:', error)
              
              // Handle error khusus untuk "Member not have enough point"
              if (error.response?.data?.message === 'VOUCHER_REDEEM.CREATE_SINGLE_DATA.MODEL.EXCEPTION' &&
                  error.response?.data?.error === 'Member not have enough point to redeem this voucher') {
                  redeemStatus.value = 'error'
                  // Tetap tampilkan modal error untuk menunjukkan pesan
              } else if (!error.response) {
                  store.dispatch('network/probe');
                  const { showToast } = useToast();
                  showToast({
                      type: 'warning',
                      title: 'No Connection',
                      message: 'Failed to redeem voucher due to connection issue. Please check your internet connection.',
                  });
                  redeemStatus.value = null;
              } else {
                  redeemStatus.value = null
                  setSwipeBackEnabled(true)
              }
          } finally {
              loading.value = false
          }
      }

      const closeRedeemStatus = () => {
          redeemStatus.value = null
          setSwipeBackEnabled(true)
      }

      // Utility methods
      const getMerchantLogo = (voucher) => {
          // Cek apakah ada logo dari API response
          let logo = voucher?.merchant?.logo?.image_url || 
                    voucher?.merchant?.logo ||
                    voucher?.merchant_logo ||
                    voucher?.corporate?.logo?.image_url ||
                    voucher?.corporate?.logo ||
                    voucher?.branch?.logo?.image_url ||
                    voucher?.branch?.logo ||
                    null;
          
          // Jika tidak ada, coba build URL S3 dari corporate_id
          if (!logo) {
              // Ambil corporate_id dari berbagai sumber termasuk localStorage
              const corporateId = voucher?.corporate_id || 
                                 voucher?.merchant?.corporate_id ||
                                 voucher?.branch?.corporate_id ||
                                 localStorage.getItem('corporateId');
              
              if (corporateId) {
                  const s3BaseUrl = 'https://s3.ap-southeast-1.amazonaws.com/migration-cdn-member.omnihotelier.com';
                  logo = `${s3BaseUrl}/user_assets/${corporateId}/corporateLogo.png`;
              }
          }
          
          return logo;
      }

      const getInitials = (voucher) => {
          const name = voucher.provider_name || voucher.corporate_name || voucher.merchant_name || voucher.merchant?.name || ''
          return name ? name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) : '?'
      }

      const getMerchantMarqueeText = (voucher) => {
          if (voucher.merchant_scope === 'all_corporate') {
              return 'All Merchants & Branches';
          }
          if (Array.isArray(voucher.eligible_merchants_info) && voucher.eligible_merchants_info.length > 1) {
              return `Claimable at ${voucher.eligible_merchants_info.length} Locations`;
          }
          if (Array.isArray(voucher.allowed_merchants) && voucher.allowed_merchants.length > 1) {
              return `Claimable at ${voucher.allowed_merchants.length} Locations`;
          }
          const merchant = voucher.provider_name || voucher.corporate_name || voucher.merchant_name || voucher.merchant?.name
          const branch = voucher.branch_name || voucher.merchant?.branch?.name || voucher.branch?.name
          return merchant && branch ? `${merchant} - ${branch}` : merchant || branch || ''
      }

      // Watchers
      watch(
          () => selected.branch_id,
          newVal => {
              if (newVal == "all") {
                  store.commit('ClientDeals/clear_filter')
                  loadState()
              }
              filterOptions.merchant = '';
          }
      )

      watch(
            () => filterOptions.branch,
            (newVal) => {
            if (newVal === '') {
                filterOptions.merchant = ''
            }
        }
      )

      // Event listeners
      window.addEventListener('resize', () => {
          isMobile.value = window.innerWidth < 992
      })

      // Lifecycle hooks
      onBeforeUnmount(() => {
          document.body.style.overflow = ''
      })

      watch([isFilterModalOpen, isSortModalOpen], (vals) => {
          const anyOpen = vals.some(Boolean)
          document.body.style.overflow = anyOpen ? 'hidden' : ''
      })

      onMounted(() => {
            const _meta = { ...meta.value, page: 1 }
            store.commit('ClientDeals/switch_page', { meta: _meta })

            //ambil query param dari url
            const urlParams = new URLSearchParams(window.location.search);
            //buat kondisi jika ada key token di urlParams, hilangkan key token nya kemudian replace url nya
            if (urlParams.has('token')) {
                urlParams.delete('token');
                window.history.replaceState({}, document.title, window.location.pathname);
            }
          loadBranches()
          loadState()
      })

      return {
          page,
          meta,
          loadState,
          deals,
          navPage,
          branches,
          selected,
          merchants,
          loadMerchant,
          filter,
          clearFilter,
          memberPoint,
          isMobile,
          openModal,
          closeModal,
          filterOptions,
          sortOptions,
          isFilterModalOpen,
          isSortModalOpen,
          branchMerchantList,
          uniqueBranches,
          filteredMerchants,
          applyFilter,
          filteredVoucherState,
          displayedVoucher,
          sortDirectionOptions,
          sortByOptions,
          applySort,
          clearSort,
          // New methods and states
          selectedVoucher,
          redeemStatus,
          loading,
          showVoucherDetail,
          closeVoucherDetail,
          handleRedeem,
          closeRedeemStatus,
          getMerchantLogo,
          getInitials,
          getMerchantMarqueeText,
          isPointSystemActive
      }
    }  
}
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.page-container {
    width: 100%;
    min-height: 100vh;
}

/* Desktop View */
.desktop-view {
    margin: 0 auto;
    padding: 24px;
}

.page-header {
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 14px;
    color: #6B7280;
}

.content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.main-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.sidebar {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.filter-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 20px;
}

.filter-group {
    margin-bottom: 20px;
}

.filter-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

.custom-select.desktop {
    position: relative;
}

.custom-select.desktop select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: white;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: all 0.2s;
}

.custom-select.desktop select:hover {
    border-color: #D1D5DB;
}

.custom-select.desktop select:focus {
    border-color: var(--primary-border, #F27178);
    box-shadow: 0 0 0 3px var(--primary-light, rgba(242, 113, 120, 0.15));
}

.select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6B7280;
}

.btn-apply,
.btn-clear {
    flex: 1;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-apply {
    background-color: var(--primary-color, #F27178);
    color: white;
}

.btn-apply:hover {
    opacity: 0.9;
}

.btn-apply:disabled {
    background-color: var(--secondary-color, #FEE9E9);
    cursor: not-allowed;
}

.btn-clear {
    background-color: white;
    color: #374151;
    border: 1px solid #E5E7EB;
}

.btn-clear:hover {
    background-color: #F9FAFB;
}

.btn-clear:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Loading Spinner */
/* Loading removed - using skeleton instead */

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 12px;
}

.empty-state p {
    font-size: 14px;
    color: #6B7280;
}

/* Pagination */
.pagination-container {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e5e7eb;
}

.pagination-container.mobile {
    margin-bottom: 80px; /* Space for bottom nav */
}

/* Modal Styles (Mobile Only) */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.modal-content {
    background: white;
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.close-button {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #6B7280;
}

.modal-body {
    padding: 20px;
}

.modal-body .filter-group {
    margin-bottom: 20px;
}

.modal-body .filter-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

.modal-body .custom-select {
    position: relative;
}

.modal-body .custom-select select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: white;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: all 0.2s;
}

.modal-body .custom-select select:hover {
    border-color: #D1D5DB;
}

.modal-body .custom-select select:focus {
    border-color: var(--primary-border, #F27178);
    box-shadow: 0 0 0 3px var(--primary-light, rgba(242, 113, 120, 0.15));
}

.button-group {
    display: flex;
    gap: 12px;
    margin-top: 24px;
}

/* Deal Cards */
.deals-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

/* Untuk layar 400px - 900px: dynamic columns */
@media (min-width: 400px) and (max-width: 900px) {
    .deals-list {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
}

.deals-list.desktop {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
}

.deal-container {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
    background-color: white;
    border: 1px solid #E1E4EA;
    cursor: pointer;
    transition: all 0.2s;
}

/* Mobile: set width for flex layout */
@media (max-width: 900px) {
    .deal-container {
        width: calc(50% - 6px);
    }
}

/* Hover effect only for desktop */
@media (min-width: 901px) {
    .deal-container:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
}

.deal-cost {
    display: flex;
    padding: 8px;
    background-color: var(--primary-color, #F27178);
    border-radius: 12px 0 12px 0;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
}

.deal-cost-text {
    font-size: 8px;
    font-weight: bold;
    color: white;
    line-height: 100%;
}

.img-container {
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    overflow: hidden;
}

/* Desktop: 164px */
@media (min-width: 1025px) {
    .deals-list.desktop .img-container {
        height: 164px;
    }
}

/* Tablet: 132px */
@media (min-width: 901px) and (max-width: 1024px) {
    .deals-list.desktop .img-container {
        height: 132px;
    }
}

.deal-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.deal-header {
    display: flex;
    gap: 8px;
    width: 100%;
    padding-left: 4px;
    padding-right: 4px;
}

.corporate-logo {
    width: 32px;
    min-width: 32px;
    height: 32px;
    min-height: 32px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.corporate-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.logo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: white;
    background-color: var(--primary-color, #F27178);
}

.deal-title-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.deal-title {
    font-size: 12px !important;
    color: black !important;
    font-weight: 520 !important;
    margin: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.deal-description {
    font-size: 10px;
    color: #717784;
    font-weight: normal;
    margin: 0 !important;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
}

.deal-description .marquee {
    display: inline-block;
    position: relative;
    margin: 0 !important;
    white-space: nowrap;
    animation: marquee 15s linear infinite;
    will-change: transform;
}

@keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100%); }
}

.deal-description .marquee::after {
    content: attr(data-text);
    position: absolute;
    left: 100%;
    top: 0;
    white-space: nowrap;
    padding-left: 24px;
}

.deal-dash-line {
    border: none;
    border-top: 1px dashed #E1E4EA;
    margin: 0 8px;
    position: relative;
}

.deal-status {
    display: flex;
    gap: 8px;
    justify-content: start;
    align-items: center;
    padding-left: 4px;
    padding-right: 4px;
}

.deal-status-text {
    font-size: 10px;
    line-height: 100%;
    color: black;
}

.deal-status-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: var(--primary-color, #F27178);
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    flex-shrink: 0;
}

/* Mobile Specific Styles */
.top-background {
    position: absolute;
    top: -8px;
    left: -32px;
    color: var(--primary-color, #FC6666);
}

.mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 64px;
    margin-bottom: 14px;
}

.mobile-title {
    font-size: 18px;
    font-weight: 520;
    margin: 0 !important;
}

.mobile-actions {
    display: flex;
    gap: 8px;
}

.filter-button {
    background-color: var(--primary-color, #F27178);
    border: none;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background-color 0.3s;
}

.filter-button img {
    width: 14px;
    height: 14px;
}

p {
    margin: 0;
}

/* Responsive Design */
/* Mobile: < 400px - design sudah bagus, jangan diubah */
@media (max-width: 399px) {
    .mobile-view {
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
    }
    
    .deals-list {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
    
    .deal-container {
        width: calc(50% - 6px);
    }
}

/* Mobile & Tablet: < 992px */
@media (max-width: 991px) {
    .mobile-view {
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 80px);
        padding-bottom: 96px;
    }
    .deals-list {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
    
    .deal-container {
        width: calc(50% - 6px);
    }
    
    .desktop-view {
        display: none;
    }
}

/* Tablet & Medium Desktop: 992px - 1280px */
@media (min-width: 992px) and (max-width: 1280px) {
    .desktop-view {
        padding: 16px;
    }
    
    .mobile-view {
        display: none;
    }
    
    .desktop-view {
        display: block;
    }
    
    .content-wrapper {
        grid-template-columns: 1fr 280px;
        gap: 16px;
    }

    .filter-card {
        padding: 16px;
    }

    .deals-list.desktop {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
    
    .deals-list.desktop .deal-container {
        width: 100%;
    }
}

/* Desktop: > 1024px - design sudah bagus, jangan diubah */
@media (min-width: 1025px) {
    .mobile-view {
        display: none;
    }
    
    .desktop-view {
        display: block;
    }
    
    .deals-list.desktop .deal-container {
        width: 100%;
    }
}
</style>